import { useState, useMemo } from "react"
import { Survey } from "@/services/surveysListService"

/** Columns that support sorting in the survey tables. */
export type SortableKey = "year" | "surveyType"

/** Sort direction: ascending or descending. */
export type SortDirection = "asc" | "desc"

/** Current sort state — null means no active sort. */
export interface SortConfig {
  key: SortableKey
  direction: SortDirection
}

/**
 * Hook providing client-side sorting for survey tables.
 *
 * Supports toggling sort on "year" and "surveyType" (locale string
 * comparison; years are four-digit strings, so this orders them
 * chronologically). Clicking the same column cycles:
 * asc → desc → no sort. Clicking a different column starts at asc.
 *
 * Also provides a `getRowKey` helper that returns a stable, unique React key
 * for each row. This is necessary because the survey data can contain
 * duplicate `id` values (a backend data integrity issue), so `id` alone is
 * not safe to use as a React key. The key combines the survey `id` with the
 * row's position in the original (unsorted) array. Using object identity to
 * look up the original position keeps the key stable across re-sorts.
 *
 * @param surveys - The array of surveys to sort.
 * @returns An object containing the sorted array, current sort config,
 *          the toggle function, and a stable row-key generator.
 */
export function useSurveySort(surveys: Survey[]): {
  sortedSurveys: Survey[]
  sortConfig: SortConfig | null
  toggleSort: (key: SortableKey) => void
  getRowKey: (survey: Survey) => string
} {
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null)

  // Map each survey object (by reference) to its index in the original array.
  // Object identity is used as the map key so the lookup remains valid after
  // the array is sorted (sorting reorders references but preserves them).
  const originalIndexBySurvey = useMemo((): Map<Survey, number> => {
    return new Map<Survey, number>(surveys.map((survey: Survey, index: number) => [survey, index]))
  }, [surveys])

  const sortedSurveys = useMemo((): Survey[] => {
    if (!sortConfig) return surveys

    return [...surveys].sort((a: Survey, b: Survey): number => {
      const aVal = a[sortConfig.key]
      const bVal = b[sortConfig.key]

      // String() guards against a survey missing the field
      const comparison: number = String(aVal).localeCompare(String(bVal))

      return sortConfig.direction === "asc" ? comparison : -comparison
    })
  }, [surveys, sortConfig])

  /**
   * Toggle sort for a given column key.
   *
   * Cycles through: asc → desc → no sort (for the same column).
   * Switches to asc when selecting a different column.
   *
   * @param key - The column key to sort by.
   */
  const toggleSort = (key: SortableKey): void => {
    setSortConfig((prev: SortConfig | null): SortConfig | null => {
      if (prev?.key !== key) {
        // Different column: start ascending
        return { key, direction: "asc" }
      }
      if (prev.direction === "asc") {
        // Same column, was ascending: switch to descending
        return { key, direction: "desc" }
      }
      // Same column, was descending: clear sort
      return null
    })
  }

  /**
   * Produce a stable, unique React key for a survey row.
   *
   * Combines the survey `id` with its original array index. The index
   * guarantees uniqueness even when the backend returns duplicate `id`
   * values, while object-identity lookup keeps the key stable across sorts.
   *
   * @param survey - The survey to generate a key for.
   * @returns A unique string key for use as a React `key` prop.
   */
  const getRowKey = (survey: Survey): string => {
    const originalIndex = originalIndexBySurvey.get(survey)
    return `${survey.mapSurveyId}-${originalIndex}`
  }

  return { sortedSurveys, sortConfig, toggleSort, getRowKey }
}
